/**
 * Admin « Boîte à outils » : tous les liens SKILLCARDS ⇄ outils (table
 * `card_tools`, migration 0551), avec la carte reliée (titre, domaine,
 * niveaux, statut). L'admin lit toutes les cartes (RLS de `cards`), donc tous
 * les liens. Lecture par pages de 1000 lignes (limite PostgREST).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// table postérieure à types.generated.ts (stale volontaire, cf. CLAUDE.md)
const db = supabase as unknown as SupabaseClient;

export interface ToolLinkedCard {
  id: string;
  title: string;
  shortCode: string | null;
  domain: string | null;
  status: string | null;
  levels: string[];
  /** rang de l'outil dans la liste de la carte (0 = en tête) */
  position: number;
}

interface Row {
  tool_id: string;
  position: number;
  cards: {
    id: string;
    title: string;
    short_code: string | null;
    domain_label: string | null;
    status: string | null;
    card_levels: { level: string }[] | null;
  } | null;
}

export const adminToolLinksQueryKey = ['admin-toolbox-links'] as const;

const PAGE = 1000;

/** cartes reliées, par outil (cartes triées par titre) */
export function useAdminToolLinks() {
  return useQuery({
    queryKey: adminToolLinksQueryKey,
    queryFn: async (): Promise<Record<string, ToolLinkedCard[]>> => {
      const rows: Row[] = [];
      for (let from = 0; ; from += PAGE) {
        const { data, error } = await db
          .from('card_tools')
          .select(
            'tool_id, position, cards(id, title, short_code, domain_label, status, card_levels(level))',
          )
          .order('tool_id')
          .order('card_id')
          .range(from, from + PAGE - 1);
        if (error) throw new Error(error.message);
        const page = (data ?? []) as unknown as Row[];
        rows.push(...page);
        if (page.length < PAGE) break;
      }
      return groupLinks(rows);
    },
    staleTime: 60_000,
  });
}

export function groupLinks(rows: readonly Row[]): Record<string, ToolLinkedCard[]> {
  const out: Record<string, ToolLinkedCard[]> = {};
  for (const r of rows) {
    if (!r.cards) continue;
    (out[r.tool_id] ??= []).push({
      id: r.cards.id,
      title: r.cards.title,
      shortCode: r.cards.short_code,
      domain: r.cards.domain_label,
      status: r.cards.status,
      levels: (r.cards.card_levels ?? []).map((l) => l.level),
      position: r.position,
    });
  }
  for (const list of Object.values(out)) list.sort((a, b) => a.title.localeCompare(b.title, 'fr'));
  return out;
}
